import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Plus, X as XIcon } from 'lucide-react';
import { parseHHMM, formatHHMM } from './timelineGeometry';

/**
 * ClipInspector — right-rail editor for the selected clip.
 *
 * Writes flow back through `onPatch(intentId, partial)` — same wire the
 * drag/resize uses. Calls are debounced at the parent (500ms) so typing
 * in the name field doesn't spam PATCHes.
 *
 * Local mirror state avoids React losing cursor position inside an
 * <input> when the parent re-renders on every keystroke.
 */
export default function ClipInspector({ intent, library = [], onPatch, onDelete }) {
  const [draft, setDraft] = useState({
    name: intent.name,
    track: intent.track,
    startTime: intent.startTime,
    endTime: intent.endTime,
    overlapPolicy: intent.overlapPolicy,
  });

  // Sync from parent whenever the intent row changes (e.g. drag finished,
  // or a socket update reconciled). We only reset fields the user isn't
  // actively typing in — if we blasted the whole draft we'd stomp
  // mid-keystroke edits.
  useEffect(() => {
    setDraft((d) => ({
      name: document.activeElement?.name === 'clip-name' ? d.name : intent.name,
      track: intent.track,
      startTime: document.activeElement?.name === 'clip-start' ? d.startTime : intent.startTime,
      endTime: document.activeElement?.name === 'clip-end' ? d.endTime : intent.endTime,
      overlapPolicy: intent.overlapPolicy,
    }));
  }, [intent]);

  const applyPatch = (partial) => {
    onPatch?.(intent.id, partial);
  };

  const handleNameBlur = () => {
    const next = draft.name.trim() || 'Untitled clip';
    if (next !== intent.name) applyPatch({ name: next });
  };

  const handleTimeBlur = (field) => {
    const raw = draft[field];
    const parsed = parseHHMM(raw);
    if (parsed === null) {
      // Revert visual to last known-good value if user typed garbage.
      setDraft((d) => ({ ...d, [field]: intent[field] }));
      return;
    }
    // Prevent end <= start.
    if (field === 'startTime' && parsed >= intent.endMinutes) {
      setDraft((d) => ({ ...d, startTime: intent.startTime }));
      return;
    }
    if (field === 'endTime' && parsed <= intent.startMinutes) {
      setDraft((d) => ({ ...d, endTime: intent.endTime }));
      return;
    }
    const normalized = formatHHMM(parsed);
    setDraft((d) => ({ ...d, [field]: normalized }));
    if (normalized !== intent[field]) applyPatch({ [field]: normalized });
  };

  return (
    <div className="timeline-editor__inspector">
      <div className="timeline-editor__inspector-header">
        <h3 className="timeline-editor__inspector-title">Clip</h3>
        <button
          type="button"
          className="timeline-editor__inspector-delete"
          aria-label="Delete clip"
          onClick={() => onDelete?.(intent.id)}
        >
          <Trash2 size={14} />
          <span>Delete</span>
        </button>
      </div>

      <div className="timeline-editor__field">
        <label htmlFor="clip-name-input">Name</label>
        <input
          id="clip-name-input"
          name="clip-name"
          type="text"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          onBlur={handleNameBlur}
        />
      </div>

      <div className="timeline-editor__field-row">
        <div className="timeline-editor__field">
          <label htmlFor="clip-start-input">Start</label>
          <input
            id="clip-start-input"
            name="clip-start"
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            value={draft.startTime}
            onChange={(e) => setDraft((d) => ({ ...d, startTime: e.target.value }))}
            onBlur={() => handleTimeBlur('startTime')}
          />
        </div>
        <div className="timeline-editor__field">
          <label htmlFor="clip-end-input">End</label>
          <input
            id="clip-end-input"
            name="clip-end"
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            value={draft.endTime}
            onChange={(e) => setDraft((d) => ({ ...d, endTime: e.target.value }))}
            onBlur={() => handleTimeBlur('endTime')}
          />
        </div>
      </div>

      <div className="timeline-editor__field">
        <label htmlFor="clip-policy-input">Overlap policy</label>
        <select
          id="clip-policy-input"
          name="clip-policy"
          value={draft.overlapPolicy}
          onChange={(e) => {
            const v = e.target.value === 'replace' ? 'replace' : 'merge';
            setDraft((d) => ({ ...d, overlapPolicy: v }));
            applyPatch({ overlapPolicy: v });
          }}
        >
          <option value="merge">merge — loop with others</option>
          <option value="replace">replace — takeover</option>
        </select>
      </div>

      <ContentPicker
        selected={intent.contentIds}
        library={library}
        onChange={(next) => applyPatch({ contentIds: next })}
      />

      <p className="timeline-editor__inspector-hint">
        Edits save automatically.
      </p>
    </div>
  );
}

/**
 * Inline content multi-picker — shows the currently assigned content
 * (with thumbnails) + an "Add content" dropdown of unassigned library
 * items. The server requires contentIds to be non-empty, so we refuse to
 * remove the last item without a confirmation via deleting the whole clip.
 */
function ContentPicker({ selected = [], library = [], onChange }) {
  const [showAdd, setShowAdd] = useState(false);

  const byId = useMemo(() => {
    const m = new Map();
    for (const item of library) {
      if (item?.contentId) m.set(item.contentId, item);
    }
    return m;
  }, [library]);

  const unassigned = useMemo(() => {
    const sel = new Set(selected);
    return library.filter((item) => !sel.has(item?.contentId));
  }, [library, selected]);

  const handleRemove = (id) => {
    if (selected.length <= 1) return; // server rejects empty
    onChange(selected.filter((x) => x !== id));
  };

  const handleAdd = (id) => {
    if (!id || selected.includes(id)) return;
    onChange([...selected, id]);
    setShowAdd(false);
  };

  return (
    <div className="timeline-editor__field">
      <label>Content</label>
      <ul className="timeline-editor__content-list">
        {selected.map((id) => {
          const meta = byId.get(id);
          return (
            <li key={id} className="timeline-editor__content-item">
              {meta?.thumbnailUrl ? (
                <img
                  className="timeline-editor__content-thumb"
                  src={meta.thumbnailUrl}
                  alt=""
                  loading="lazy"
                />
              ) : (
                <div className="timeline-editor__content-thumb timeline-editor__content-thumb--empty" />
              )}
              <span className="timeline-editor__content-name">
                {meta?.title || meta?.name || id}
              </span>
              {selected.length > 1 && (
                <button
                  type="button"
                  className="timeline-editor__content-remove"
                  onClick={() => handleRemove(id)}
                  aria-label={`Remove ${meta?.title || id}`}
                >
                  <XIcon size={11} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {showAdd ? (
        <select
          className="timeline-editor__content-add-select"
          defaultValue=""
          onChange={(e) => handleAdd(e.target.value)}
          onBlur={() => setShowAdd(false)}
          autoFocus
        >
          <option value="" disabled>Select content…</option>
          {unassigned.map((item) => (
            <option key={item.contentId} value={item.contentId}>
              {item.title || item.name || item.contentId}
            </option>
          ))}
        </select>
      ) : (
        unassigned.length > 0 && (
          <button
            type="button"
            className="timeline-editor__content-add-btn"
            onClick={() => setShowAdd(true)}
          >
            <Plus size={12} /> Add content
          </button>
        )
      )}
    </div>
  );
}
